/**
 * CREATIVE DETONATOR - Application State Store
 * Centralized state management with subscriptions
 *
 * @version 1.0.0
 * @description Implements FRONTEND-30 (State Management Consolidation)
 */

// ============================================================================
// STATE CONTAINER
// ============================================================================

/**
 * Complete application state
 * Phase 2: Expanded state shape with all required fields
 * @type {Object}
 */
const state = {
  // Authentication
  auth: {
    user: null,
    profile: null,
    isAuthenticated: false,
    loading: true,
    error: null
  },

  // Generator (4-phase wizard)
  generator: {
    phase: 1,
    brief: {
      product: '',
      benefit: '',
      audience: '',
      tone: '',
      formats: [],            // Also stored here for backwards compatibility
      productId: null,
      productData: null
    },
    brand: null,              // Loaded brand cartridge
    formats: [],              // Selected formats (also in brief for compat)
    triggers: [],             // Selected trigger codes
    ideas: [],                // Generated ideas (normalized)
    currentIdeaId: null,      // Selected idea for canvas
    isGenerating: false,      // Generation in progress
    generationProgress: {
      current: 0,
      total: 0,
      currentFormat: null,
      currentTrigger: null
    },
    sessionId: null,          // Current session ID
    loading: false,
    error: null
  },

  // Dashboard
  dashboard: {
    ideas: [],                // All user ideas
    sessions: [],             // All sessions
    brands: [],               // Brand cartridges
    currentSessionId: null,
    filters: {
      format: 'all',
      saved: false,
      search: ''
    },
    selectedIds: [],          // Multi-select (array for serialization)
    loading: false,
    error: null
  },

  // UI State
  ui: {
    sidebar: {
      open: false,
      collapsed: false,
      activeSection: null
    },
    modal: {
      type: null,
      data: null
    },
    modals: {},               // Named modals: { name: { open: boolean, data: any } }
    toast: {
      message: null,
      type: 'info',
      visible: false
    },
    theme: 'dark'
  },

  // Presentation
  presentation: {
    active: false,
    ideas: [],
    currentIndex: 0,
    fullscreen: false
  }
};

// Initial state snapshot for reset
const initialState = JSON.parse(JSON.stringify(state));

// ============================================================================
// SUBSCRIPTIONS
// ============================================================================

const subscribers = new Map();
let subscriberId = 0;

/**
 * Subscribe to state changes
 * @param {string} path - Dot-notation path to watch (e.g., 'generator.phase', '*' for all)
 * @param {Function} callback - Called with (newValue, oldValue, path)
 * @returns {Function} Unsubscribe function
 *
 * @example
 * // Subscribe to specific path
 * const unsub = subscribe('generator.phase', (phase, oldPhase) => {
 *   console.log(`Phase changed: ${oldPhase} -> ${phase}`);
 * });
 *
 * // Subscribe to parent (notified of any child change)
 * subscribe('generator', (generator, _, changedPath) => {
 *   console.log(`Generator changed at ${changedPath}`);
 * });
 *
 * // Subscribe to all changes
 * subscribe('*', (state, _, path) => {
 *   console.log(`State changed at ${path}`);
 * });
 */
export function subscribe(path, callback) {
  const id = ++subscriberId;

  if (!subscribers.has(path)) {
    subscribers.set(path, new Map());
  }

  subscribers.get(path).set(id, callback);

  // Return unsubscribe function
  return () => {
    const pathSubscribers = subscribers.get(path);
    if (pathSubscribers) {
      pathSubscribers.delete(id);
      if (pathSubscribers.size === 0) {
        subscribers.delete(path);
      }
    }
  };
}

/**
 * Subscribe to multiple paths with single callback
 * @param {string[]} paths - Array of paths to watch
 * @param {Function} callback - Single callback for all paths
 * @returns {Function} Unsubscribe function
 */
export function subscribeMultiple(paths, callback) {
  const unsubscribers = paths.map(path => subscribe(path, callback));
  return () => unsubscribers.forEach(unsub => unsub());
}

/**
 * Notify subscribers of change
 * @private
 */
function notify(path, newValue, oldValue) {
  // Notify exact path subscribers
  const exact = subscribers.get(path);
  if (exact) {
    exact.forEach(callback => {
      try {
        callback(newValue, oldValue, path);
      } catch (e) {
        console.error(`[Store] Subscriber error for ${path}:`, e);
      }
    });
  }

  // Notify parent path subscribers (e.g., 'generator' for 'generator.phase')
  const parts = path.split('.');
  while (parts.length > 1) {
    parts.pop();
    const parentPath = parts.join('.');
    const parent = subscribers.get(parentPath);
    if (parent) {
      const parentValue = get(parentPath);
      parent.forEach(callback => {
        try {
          callback(parentValue, null, path);
        } catch (e) {
          console.error(`[Store] Parent subscriber error for ${parentPath}:`, e);
        }
      });
    }
  }

  // Notify root subscribers
  const root = subscribers.get('*');
  if (root) {
    root.forEach(callback => {
      try {
        callback(state, null, path);
      } catch (e) {
        console.error('[Store] Root subscriber error:', e);
      }
    });
  }
}

// ============================================================================
// STATE ACCESS
// ============================================================================

/**
 * Get state value by path
 * @param {string} path - Dot-notation path (e.g., 'generator.brief.product')
 * @returns {any} Value at path or undefined
 *
 * @example
 * get('generator.phase')  // 1
 * get('auth.user.email')  // 'user@example.com'
 * get()                   // entire state
 */
export function get(path) {
  if (!path) return state;

  return path.split('.').reduce((obj, key) => {
    return obj && obj[key] !== undefined ? obj[key] : undefined;
  }, state);
}

/**
 * Set state value by path
 * @param {string} path - Dot-notation path
 * @param {any} value - New value
 *
 * @example
 * set('generator.phase', 2);
 * set('auth.user', { id: 1, email: 'test@example.com' });
 */
export function set(path, value) {
  const parts = path.split('.');
  const key = parts.pop();
  const parent = parts.reduce((obj, k) => {
    if (obj[k] === undefined) {
      obj[k] = {};
    }
    return obj[k];
  }, state);

  const oldValue = parent[key];

  // Skip if no change (shallow comparison)
  if (oldValue === value) return;

  // For objects, do deep comparison
  if (typeof oldValue === 'object' && typeof value === 'object' &&
      JSON.stringify(oldValue) === JSON.stringify(value)) {
    return;
  }

  // Update state
  parent[key] = value;

  // Notify subscribers
  notify(path, value, oldValue);

  // Auto-persist if configured (debounced)
  if (shouldPersist(path)) {
    scheduleSave();
  }
}

/**
 * Update state with partial object (merge)
 * @param {string} path - Dot-notation path
 * @param {Object} partial - Partial object to merge
 *
 * @example
 * update('generator.brief', { product: 'New Product' });
 * // Only updates product, keeps other brief properties
 */
export function update(path, partial) {
  const current = get(path);

  if (typeof current !== 'object' || current === null || Array.isArray(current)) {
    set(path, partial);
  } else {
    set(path, { ...current, ...partial });
  }
}

/**
 * Reset state path to initial value
 * @param {string} path - Path to reset (or empty for entire state)
 */
export function reset(path) {
  if (!path) {
    // Reset entire state
    Object.keys(state).forEach(key => {
      set(key, JSON.parse(JSON.stringify(initialState[key])));
    });
    return;
  }

  const initial = path.split('.').reduce((obj, key) => {
    return obj && obj[key] !== undefined ? obj[key] : undefined;
  }, initialState);

  if (initial !== undefined) {
    set(path, JSON.parse(JSON.stringify(initial)));
  }
}

// ============================================================================
// PERSISTENCE
// ============================================================================

/**
 * Mixed storage persistence configuration
 * Phase 2: localStorage for persistent data, sessionStorage for workflow data
 *
 * IMPORTANT: localStorage data survives browser restarts
 * sessionStorage data clears when tab closes
 */
const PERSIST_CONFIG = {
  version: 2,  // Bump to clear old storage format
  debounceMs: 100,

  // PERSISTENT DATA (localStorage) - User's creative work survives restarts
  localStorage: {
    key: 'cd_app_state_persistent',
    paths: [
      // User content - MUST persist across browser restarts
      'dashboard.ideas',           // Generated ideas (capped to prevent quota issues)
      'dashboard.brands',          // Brand cartridges
      // 'dashboard.sessions' removed - too large, fetched from API
      // Note: We only cache recent sessions, not all of them

      // User preferences - SHOULD persist
      'ui.theme',
      'ui.sidebar.collapsed'
    ],
    // Max items to store for arrays (prevents localStorage quota exceeded)
    limits: {
      'dashboard.ideas': 100,      // Keep last 100 ideas
      'dashboard.brands': 50       // Keep last 50 brands
    }
  },

  // WORKFLOW DATA (sessionStorage) - Temporary, clears on tab close
  sessionStorage: {
    key: 'cd_app_state_session',
    paths: [
      // Generator wizard state - Only needed during active session
      'generator.phase',
      'generator.brief',
      'generator.formats',
      'generator.triggers',
      'generator.ideas',            // Generated but not yet saved
      'generator.brand',            // Loaded brand for this session
      'generator.currentIdeaId',
      'generator.sessionId',

      // Temporary UI state
      'dashboard.filters',
      'dashboard.currentSessionId',
      'dashboard.selectedIds',
      'ui.sidebar.open'
    ]
  }
};

// Track all persist paths for quick lookup
const allPersistPaths = new Set([
  ...PERSIST_CONFIG.localStorage.paths,
  ...PERSIST_CONFIG.sessionStorage.paths
]);

// Debounced save
let saveTimeout = null;

/**
 * Schedule a debounced save
 * @private
 */
function scheduleSave() {
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveState();
  }, PERSIST_CONFIG.debounceMs);
}

/**
 * Check if path should be persisted
 * @private
 */
function shouldPersist(path) {
  for (const persistPath of allPersistPaths) {
    if (path.startsWith(persistPath)) return true;
  }
  return false;
}

/**
 * Set nested value in object
 * @private
 */
function setNestedValue(obj, path, value) {
  const parts = path.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!current[parts[i]]) current[parts[i]] = {};
    current = current[parts[i]];
  }
  current[parts[parts.length - 1]] = value;
}

/**
 * Get nested value from object
 * @private
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

/**
 * Apply limits to array values before saving
 * @private
 */
function applyLimits(value, path, limits) {
  if (!limits || !limits[path]) return value;
  if (!Array.isArray(value)) return value;
  const limit = limits[path];
  if (value.length > limit) {
    // Keep most recent items (assuming newest are at end or have recent dates)
    return value.slice(-limit);
  }
  return value;
}

/**
 * Save state to storage
 * @private
 */
function saveState() {
  try {
    // Save persistent data to localStorage
    const persistentData = {};
    for (const path of PERSIST_CONFIG.localStorage.paths) {
      let value = get(path);
      if (value !== undefined) {
        // Apply limits to prevent quota exceeded
        value = applyLimits(value, path, PERSIST_CONFIG.localStorage.limits);
        setNestedValue(persistentData, path, value);
      }
    }

    try {
      localStorage.setItem(PERSIST_CONFIG.localStorage.key, JSON.stringify({
        version: PERSIST_CONFIG.version,
        timestamp: Date.now(),
        state: persistentData
      }));
    } catch (e) {
      // localStorage quota exceeded - clear old data and retry
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        console.warn('[Store] localStorage quota exceeded, clearing old data');
        localStorage.removeItem(PERSIST_CONFIG.localStorage.key);
        // Don't retry - let it save on next change
      } else {
        throw e;
      }
    }

    // Save session data to sessionStorage
    const sessionData = {};
    for (const path of PERSIST_CONFIG.sessionStorage.paths) {
      const value = get(path);
      if (value !== undefined) {
        setNestedValue(sessionData, path, value);
      }
    }
    sessionStorage.setItem(PERSIST_CONFIG.sessionStorage.key, JSON.stringify({
      version: PERSIST_CONFIG.version,
      timestamp: Date.now(),
      state: sessionData
    }));

  } catch (err) {
    console.error('[Store] Failed to save state:', err);
  }
}

/**
 * Load state from a specific storage
 * @private
 */
function loadFromStorage(storage, config, options = {}) {
  const storageName = storage === localStorage ? 'localStorage' : 'sessionStorage';
  try {
    const raw = storage.getItem(config.key);
    if (!raw) return;

    const payload = JSON.parse(raw);

    // Version check
    if (payload.version !== PERSIST_CONFIG.version) {
      console.warn(`[Store] ${storageName} version mismatch, clearing`);
      storage.removeItem(config.key);
      return;
    }

    // Age check (if maxAge specified)
    if (options.maxAge) {
      const age = Date.now() - payload.timestamp;
      if (age > options.maxAge) {
        console.warn(`[Store] ${storageName} state too old, clearing`);
        storage.removeItem(config.key);
        return;
      }
    }

    // Restore each path
    for (const path of config.paths) {
      const value = getNestedValue(payload.state, path);
      if (value !== undefined) {
        // Direct state mutation during restore (avoid triggering subscribers/save)
        const parts = path.split('.');
        const key = parts.pop();
        let parent = state;
        for (const part of parts) {
          if (parent[part] === undefined) parent[part] = {};
          parent = parent[part];
        }
        parent[key] = value;
      }
    }

    console.log(`[Store] Restored from ${storageName}`);
  } catch (err) {
    console.error(`[Store] Failed to load from ${storageName}:`, err);
    // Clear corrupted data
    storage.removeItem(config.key);
  }
}

/**
 * Restore persisted values from both storage types
 */
export function restore() {
  // Load persistent data from localStorage (survives restart)
  loadFromStorage(localStorage, PERSIST_CONFIG.localStorage, {
    maxAge: null  // No age limit for persistent data
  });

  // Load session data from sessionStorage (current session only)
  loadFromStorage(sessionStorage, PERSIST_CONFIG.sessionStorage, {
    maxAge: 24 * 60 * 60 * 1000  // 24 hour max age
  });
}

/**
 * Clear all persisted state
 */
export function clearPersisted() {
  try {
    localStorage.removeItem(PERSIST_CONFIG.localStorage.key);
    sessionStorage.removeItem(PERSIST_CONFIG.sessionStorage.key);
    console.log('[Store] Cleared all persisted state');
  } catch (e) {
    console.warn('[Store] Failed to clear persisted state:', e);
  }
}

/**
 * Add a path to persistence (session storage by default)
 * @param {string} path
 * @param {'localStorage'|'sessionStorage'} storageType
 */
export function addPersistPath(path, storageType = 'sessionStorage') {
  if (storageType === 'localStorage') {
    PERSIST_CONFIG.localStorage.paths.push(path);
  } else {
    PERSIST_CONFIG.sessionStorage.paths.push(path);
  }
  allPersistPaths.add(path);
  scheduleSave();
}

/**
 * Get persistence config (for debugging)
 */
export function getPersistConfig() {
  return { ...PERSIST_CONFIG };
}

// ============================================================================
// ACTIONS (Named state mutations)
// ============================================================================

const actions = new Map();

/**
 * Register an action
 * @param {string} name - Action name (e.g., 'auth/setUser')
 * @param {Function} handler - Action handler
 */
export function registerAction(name, handler) {
  actions.set(name, handler);
}

/**
 * Dispatch an action
 * @param {string} action - Action name
 * @param {any} payload - Action payload
 *
 * @example
 * dispatch('auth/setUser', user);
 * dispatch('generator/setPhase', 2);
 */
export function dispatch(action, payload) {
  const handler = actions.get(action);
  if (handler) {
    handler(payload);
  } else {
    console.warn(`[Store] Unknown action: ${action}`);
  }
}

// Register built-in actions
registerAction('auth/setUser', (user) => {
  set('auth.user', user);
  set('auth.loading', false);
});

registerAction('auth/setProfile', (profile) => {
  set('auth.profile', profile);
});

registerAction('auth/logout', () => {
  set('auth.user', null);
  set('auth.profile', null);
});

registerAction('generator/setPhase', (phase) => {
  set('generator.phase', phase);
});

registerAction('generator/updateBrief', (brief) => {
  update('generator.brief', brief);
});

registerAction('generator/setFormats', (formats) => {
  set('generator.formats', formats);
});

registerAction('generator/setTriggers', (triggers) => {
  set('generator.triggers', triggers);
});

registerAction('generator/addIdea', (idea) => {
  const ideas = get('generator.ideas') || [];
  set('generator.ideas', [...ideas, idea]);
});

registerAction('generator/setIdeas', (ideas) => {
  set('generator.ideas', ideas);
});

registerAction('generator/reset', () => {
  reset('generator');
});

registerAction('ui/toggleSidebar', () => {
  const current = get('ui.sidebar.open');
  set('ui.sidebar.open', !current);
});

registerAction('ui/openModal', ({ type, data = null }) => {
  set('ui.modal', { type, data });
});

registerAction('ui/closeModal', () => {
  set('ui.modal', { type: null, data: null });
});

registerAction('ui/showToast', ({ message, type = 'info' }) => {
  set('ui.toast', { message, type });
});

registerAction('dashboard/setFilters', (filters) => {
  update('dashboard.filters', filters);
});

// ============================================================================
// DEBUGGING
// ============================================================================

/**
 * Get full state snapshot (for debugging)
 * @returns {Object} Deep clone of state
 */
export function getSnapshot() {
  return JSON.parse(JSON.stringify(state));
}

/**
 * Log state changes (development only)
 */
export function enableLogging() {
  subscribe('*', (newState, oldState, path) => {
    console.log(`[Store] ${path}:`, get(path));
  });
}

/**
 * Dev tools integration
 */
export function getDevTools() {
  return {
    getState: getSnapshot,
    getSubscribers: () => Array.from(subscribers.keys()),
    getActions: () => Array.from(actions.keys()),
    getPersistPaths: () => Array.from(allPersistPaths),
    getPersistConfig
  };
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize the store (restore persisted state)
 */
export function init() {
  restore();
  console.log('[Store] Initialized');
}

// ============================================================================
// GLOBAL EXPORT
// ============================================================================

window.Store = {
  get,
  set,
  update,
  reset,
  subscribe,
  subscribeMultiple,
  dispatch,
  registerAction,
  getSnapshot,
  enableLogging,
  init,
  restore,
  clearPersisted,
  addPersistPath,
  getPersistConfig,
  getDevTools
};

export default {
  get,
  set,
  update,
  reset,
  subscribe,
  subscribeMultiple,
  dispatch,
  registerAction,
  getSnapshot,
  enableLogging,
  init,
  restore,
  clearPersisted,
  addPersistPath,
  getPersistConfig
};
